import React, { useCallback, useState, createContext, useContext, useEffect } from 'react'
import { Toast, ToastProps } from '../components/search-listing-save-advert/components/toast'
import { StyledToastWrapper } from '../components/search-listing-save-advert/components/toast.styles'

const TOAST_AUTODELETE_TIME = 2800

const ToastContext = createContext<(toast: ToastProps) => void>(() => {})

ToastContext.displayName = 'ToastContext'

type ToastWithTimeout = ToastProps & { timeout: NodeJS.Timeout }

export const ToastContextProvider: React.FC<React.PropsWithChildren<object>> = ({
    children,
}: React.PropsWithChildren<object>) => {
    const [toasts, setToasts] = useState<ToastWithTimeout[]>([])

    // Cleanup any remaining timeouts before unmounting to prevent leaking memory
    useEffect(() => {
        return () =>
            setToasts((prev) => {
                prev.map((toast) => clearTimeout(toast.timeout))
                return prev
            })
    }, [setToasts])

    // Each toast will be dismissed independently once its timeout is reached
    const addToast = useCallback(
        (toast: ToastProps) => {
            const toastWithTimeout: ToastWithTimeout = {
                ...toast,
                timeout: setTimeout(() => {
                    setToasts((prev) => prev.filter((singleToast) => singleToast !== toastWithTimeout))
                }, TOAST_AUTODELETE_TIME),
            }
            setToasts((toasts) => [...toasts, toastWithTimeout])
        },
        [setToasts]
    )

    return (
        <ToastContext.Provider value={addToast}>
            {children}
            <StyledToastWrapper>
                {toasts.map((toast, index) => (
                    <Toast key={`${index}-${toast.message}`} {...toast} />
                ))}
            </StyledToastWrapper>
        </ToastContext.Provider>
    )
}

export const useToastContext = () => {
    return useContext(ToastContext)
}
